import api from './api';
import storageService from './storageService';

/**
 * Auth Service - Handles all authentication related API calls
 */
const authService = {
  /**
   * Get captcha image for login form
   * @returns {Promise} Resolves to captcha image data
   */
  getCaptcha: async () => {
    const response = await api.get('auth/captcha');
    return response.data;
  },

  /**
   * Login user
   * @param {Object} credentials - User credentials
   * @param {string} credentials.username - User's email or username
   * @param {string} credentials.password - User's password
   * @param {string} credentials.captcha - Captcha value
   * @param {string} credentials.lang - User's language preference
   * @returns {Promise} Resolves to user data or two-factor status
   */
  login: async (credentials) => {
    // Clear any existing storage before login
    storageService.clearAllStorage();
    const response = await api.post('auth/login', credentials);
    return response.data;
  },

  /**
   * Confirm two-factor authentication
   * @param {Object} twoFactorData - Two-factor data
   * @param {string} twoFactorData.code - Two-factor verification code
   * @returns {Promise} Resolves to user data
   */
  confirmTwoFactor: async (twoFactorData) => {
    const response = await api.post('auth/twofactor-confirm', twoFactorData);
    return response.data;
  },

  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @param {string} userData.email - User's email
   * @param {string} userData.phone_number - User's phone number
   * @param {string} userData.phone_code - User's phone country code
   * @param {string} userData.password - User's password
   * @param {string} userData.first_name - User's first name
   * @param {string} userData.last_name - User's last name
   * @returns {Promise} Resolves to registration result
   */
  register: async (userData) => {
    const response = await api.post('auth/register', userData);
    return response.data;
  },

  /**
   * Get current user info
   * @returns {Promise} Resolves to user data or rejects if not authenticated
   */
  getCurrentUser: async () => {
    const response = await api.get('api/user');
    return response.data;
  },

  /**
   * Get user activity data
   * @returns {Promise} Resolves to user activity data
   */
  getUserActivity: async (params) => {
    const response = await api.get('api/user/activity', { params });
    return response.data;
  },

  /**
   * Logout user
   * @returns {Promise} Resolves when user is logged out
   */
  logout: async () => {
    const response = await api.get('auth/logout');
    // Clear all storage after logout
    storageService.clearAllStorage();
    return response.data;
  },

  /**
   * Login with Google
   * @param {Object} data - Google login data
   * @returns {Promise} Resolves to user data
   */
  googleLogin: async (data) => {
    // Clear any existing storage before login
    storageService.clearAllStorage();
    const response = await api.post('auth/google-login', data);
    return response.data;
  },

  /**
   * Login with Apple
   * @param {Object} data - Apple login data
   * @returns {Promise} Resolves to user data
   */
  appleLogin: async (data) => {
    // Clear any existing storage before login
    storageService.clearAllStorage();
    const response = await api.post('auth/apple-login', data);
    return response.data;
  },

  /**
   * Get password reset link
   * @param {Object} data - Password reset data
   * @returns {Promise} Resolves to user data
   */
  getPasswordResetLink: async (data) => {
    const response = await api.post('auth/password-reset-link', data);
    return response.data;
  },

  /**
   * Reset password
   * @param {string} token - Password reset token
   * @param {string} password - New password
   * @returns {Promise} Resolves to user data
   */
  resetPassword: async ({ token, password }) => {
    const response = await api.post(`auth/password-reset/${token}`, { password });
    return response.data;
  },

  /**
   * Handle Apple Sign In callback
   * @param {Object} callbackData - Apple callback data
   * @param {string} callbackData.code - Authorization code from Apple
   * @param {string} callbackData.state - State parameter for CSRF protection
   * @returns {Promise} Resolves to user data or two-factor status
   */
  appleCallback: async (callbackData) => {
    const response = await api.post('auth/apple/callback', callbackData);
    return response.data;
  },

  /**
   * Get JWT token for widget integration
   * This endpoint should be implemented on your backend to securely generate JWT tokens
   * @returns {Promise} Resolves to JWT token string
   */
  getWidgetToken: async () => {
    const response = await api.get('auth/widget-token');
    return response.data.token || response.data;
  },
};

export default authService;